// ---------------------------------------------------------------------------
//  StepQuotePreview.jsx – Botón correo + modal PDF
// ---------------------------------------------------------------------------
import React, { useRef, useState, useMemo } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Stack,
  Box,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CloseIcon from "@mui/icons-material/Close";

import SolarProposalReceipt from "./SolarProposalReceipt";

const orange = "#E06A00";
const orangeDark = "#cc5500";

export default function StepQuotePreview({ leadData, quoteData, onSendEmail }) {
  const receiptRef = useRef(null);
  const [open, setOpen] = useState(false);
  if (!quoteData) return null;

  /* datos para el PDF */
  const receiptData = useMemo(
    () => ({
      titular: leadData?.name ?? "",
      telefono: leadData?.phone ?? "",
      email: leadData?.email ?? "",

      tarifaCFE: quoteData.tarifa,
      consumoTotalAnualKwh: quoteData.totAnualKwh,
      pagoActualAnualMxn: quoteData.totAnualCost,
      potenciaSistema: quoteData.kwPotenciaReq,
      numPaneles: quoteData.numSP585,
      produccionAnualKwh: quoteData.prodAnual,
      valorEnergiaSolarAnual: quoteData.valorEnergiaSolarAnual,
      ahorroAnualMxn: quoteData.ahorroAnual,

      precioUsd: quoteData.precioSistemaUsd,
      precioMxn: quoteData.precioSistemaMxn,
      retornoAnios: quoteData.retornoAnios,
    }),
    [leadData, quoteData]
  );

  return (
    <section className="max-w-5xl mx-auto space-y-12">
      {/* BOTONES */}
      <Stack
        direction="row"
        justifyContent="center"
        gap={3}
        pt={6}
        flexWrap="wrap"
      >
        <Button
          variant="contained"
          startIcon={<EmailIcon />}
          sx={{
            bgcolor: orange,
            "&:hover": { bgcolor: orangeDark },
            minWidth: 190,
          }}
          onClick={() => onSendEmail?.(receiptData)}
        >
          Mandar por correo
        </Button>
        <Button
          variant="outlined"
          endIcon={<PictureAsPdfIcon />}
          sx={{
            borderColor: orange,
            color: orange,
            minWidth: 200,
            "&:hover": {
              bgcolor: "#fff4e8",
              borderColor: orangeDark,
              color: orangeDark,
            },
          }}
          onClick={() => setOpen(true)}
        >
          Ver / Descargar PDF
        </Button>
      </Stack>

      {/* MODAL PDF */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle
          sx={{
            bgcolor: orange,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            pl: 3,
          }}
        >
          Vista previa de la cotización
          <IconButton
            sx={{ ml: "auto", color: "#fff" }}
            onClick={() => setOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <SolarProposalReceipt
              ref={receiptRef}
              data={receiptData}
              hideActions
            />
          </Box>
        </DialogContent>

        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            sx={{
              borderColor: orange,
              color: orange,
              "&:hover": {
                bgcolor: "#fff4e8",
                borderColor: orangeDark,
                color: orangeDark,
              },
            }}
            onClick={() => receiptRef.current?.download()}
          >
            Descargar PDF
          </Button>
        </Box>
      </Dialog>
    </section>
  );
}
